import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Row, Col, Input, Select, DatePicker } from 'antd';
import { Receivable, ReceivableStatus, ReceivableType } from '@/types/finance';
import { ResidentSelector } from '@/components/common/ResidentSelector';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import { VendorSelector } from '@/components/common/VendorSelector';
import CurrencyInput from '@/components/common/CurrencyInput';
import dayjs from 'dayjs';
import { Money } from '@/types';
import { formatFirestoreTimestamp } from '@/utils/date';

interface ReceivableModalProps {
  open: boolean;
  receivable?: Receivable | null;
  onSuccess: (data: Omit<Receivable, 'id' | 'activityRecords' | 'workspace'>) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface ReceivableFormValues {
  name: string;
  type: ReceivableType;
  status: ReceivableStatus;
  amount: Money;
  dueDate: any;
  description: string;
  notes?: string;
  receivingEntityType?: 'resident' | 'employee' | 'vendor';
  residentId?: string;
  employeeId?: string;
  vendorId?: string;
}

const ReceivableModal: React.FC<ReceivableModalProps> = ({
  open,
  receivable,
  onSuccess,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation(['receivables', 'common']);
  const [form] = Form.useForm<ReceivableFormValues>();
  const [selectedType, setSelectedType] = React.useState<ReceivableType | undefined>(receivable?.type);
  const [receivingEntityType, setReceivingEntityType] = React.useState<'resident' | 'employee' | 'vendor' | undefined>(
    receivable?.resident ? 'resident' : receivable?.employee ? 'employee' : receivable?.vendor ? 'vendor' : undefined
  );
  const [residentData, setResidentData] = React.useState<{ id: string; name: string } | null>(
    receivable?.resident ? { id: receivable.resident.id, name: receivable.resident.name } : null
  );
  const [employeeData, setEmployeeData] = React.useState<{ id: string; name: string } | null>(
    receivable?.employee ? { id: receivable.employee.id, name: receivable.employee.name } : null
  );
  const [vendorData, setVendorData] = React.useState<{ id: string; name: string } | null>(
    receivable?.vendor ? { id: receivable.vendor.id, name: receivable.vendor.name } : null
  );

  const receivableTypes = [
    { text: 'Rent', value: 'rent' },
    { text: 'Maintenance', value: 'maintenance' },
    { text: 'Utility', value: 'utility' },
    { text: 'Fine', value: 'fine' },
    { text: 'Other', value: 'other' },
  ];

  const receivableStatuses = [
    { text: 'Pending', value: 'pending' },
    { text: 'Partially Paid', value: 'partial' },
    { text: 'Paid', value: 'paid' },
    { text: 'Overdue', value: 'overdue' },
    { text: 'Cancelled', value: 'cancelled' },
  ];

  const receivingEntityTypes = [
    { text: 'Resident', value: 'resident' },
    { text: 'Employee', value: 'employee' },
    { text: 'Vendor', value: 'vendor' },
  ];

  const handleTypeChange = (type: ReceivableType) => {
    setSelectedType(type);
    
    // Set default receiving entity type based on receivable type
    if (type === 'rent' || type === 'maintenance') {
      setReceivingEntityType('resident');
      form.setFieldValue('receivingEntityType', 'resident');
      // Clear other entity data
      form.setFieldValue('employeeId', undefined);
      form.setFieldValue('vendorId', undefined);
      setEmployeeData(null);
      setVendorData(null);
    } else {
      // For other types, allow user to choose
      setReceivingEntityType(undefined);
      form.setFieldValue('receivingEntityType', undefined);
      // Clear all entity data
      form.setFieldValue('residentId', undefined);
      form.setFieldValue('employeeId', undefined);
      form.setFieldValue('vendorId', undefined);
      setResidentData(null);
      setEmployeeData(null);
      setVendorData(null);
    }
  };

  const handleReceivingEntityTypeChange = (entityType: 'resident' | 'employee' | 'vendor') => {
    setReceivingEntityType(entityType);
    // Clear all entity data when changing entity type
    form.setFieldValue('residentId', undefined);
    form.setFieldValue('employeeId', undefined);
    form.setFieldValue('vendorId', undefined);
    setResidentData(null);
    setEmployeeData(null);
    setVendorData(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const receivableData = {
        ...values,
        amount: values.amount, // Keep the full Money object with currency and amount
        dueDate: values.dueDate?.toDate(),
        resident: receivingEntityType === 'resident' && residentData ? { id: residentData.id, name: residentData.name } : undefined,
        employee: receivingEntityType === 'employee' && employeeData ? { id: employeeData.id, name: employeeData.name } : undefined,
        vendor: receivingEntityType === 'vendor' && vendorData ? { id: vendorData.id, name: vendorData.name } : undefined,
      };
      onSuccess(receivableData);
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedType(undefined);
    setReceivingEntityType(undefined);
    setResidentData(null);
    setEmployeeData(null);
    setVendorData(null);
    onCancel();
  };

  React.useEffect(() => {
    if (open && receivable) {
      // Handle Firestore timestamp conversion for dueDate using the utility function
      const dueDate = receivable.dueDate 
        ? dayjs(formatFirestoreTimestamp(receivable.dueDate))
        : undefined;

      const entityType = receivable.resident ? 'resident' : receivable.employee ? 'employee' : receivable.vendor ? 'vendor' : undefined;

      form.setFieldsValue({
        ...receivable,
        dueDate,
        receivingEntityType: entityType,
        residentId: receivable.resident?.id,
        employeeId: receivable.employee?.id,
        vendorId: receivable.vendor?.id,
      });
      setSelectedType(receivable.type);
      setReceivingEntityType(entityType);
      setResidentData(receivable.resident ? { id: receivable.resident.id, name: receivable.resident.name } : null);
      setEmployeeData(receivable.employee ? { id: receivable.employee.id, name: receivable.employee.name } : null);
      setVendorData(receivable.vendor ? { id: receivable.vendor.id, name: receivable.vendor.name } : null);
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({
        status: 'pending',
        type: 'other',
      });
      setSelectedType(undefined);
      setReceivingEntityType(undefined);
      setResidentData(null);
      setEmployeeData(null);
      setVendorData(null);
    }
  }, [open, receivable, form]);

  return (
    <Modal
      title={
        receivable
          ? t('receivables:form.updateTitle')
          : t('receivables:form.createTitle')
      }
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 'pending',
          type: 'other',
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t('common:name')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <Input placeholder={t('common:name')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('common:typeLabel')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <Select
                placeholder={t('common:select')}
                options={receivableTypes.map(t => ({ value: t.value, label: t.text }))}
                onChange={handleTypeChange}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Receiving Entity Type and Selector Row */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="receivingEntityType"
              label="Receiving From"
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <Select
                placeholder={t('common:select')}
                options={receivingEntityTypes.map(t => ({ value: t.value, label: t.text }))}
                onChange={handleReceivingEntityTypeChange}
                disabled={selectedType === 'rent' || selectedType === 'maintenance'}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* Conditional Resident Selector */}
            {receivingEntityType === 'resident' && (
              <ResidentSelector
                onResidentDataChange={setResidentData}
              />
            )}

            {/* Conditional Employee Selector */}
            {receivingEntityType === 'employee' && (
              <EmployeeSelector
                onEmployeeDataChange={setEmployeeData}
              />
            )}

            {/* Conditional Vendor Selector */}
            {receivingEntityType === 'vendor' && (
              <VendorSelector
                onVendorDataChange={setVendorData}
              />
            )}
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="amount"
              label={t('common:amount')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
                {
                  validator: (_, value) => {
                    if (!value || !value.amount || value.amount <= 0) {
                      return Promise.reject(new Error(t('common:invalid')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <CurrencyInput />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dueDate"
              label={t('common:dueDate')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <DatePicker
                placeholder={t('common:dueDate')}
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('common:statusLabel')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <Select
                placeholder={t('common:select')}
                options={receivableStatuses.map(s => ({ value: s.value, label: s.text }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label={t('common:description')}
          rules={[
            {
              required: true,
              message: t('common:fieldRequired'),
            },
          ]}
        >
          <Input.TextArea
            placeholder={t('common:description')}
            rows={3}
          />
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('common:notes')}
        >
          <Input.TextArea
            placeholder={t('common:notes')}
            rows={2}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReceivableModal; 